// Transitopia Cycling map style for MapLibre GL
// By Braden MacDonald
// Code license: MIT (see repository's LICENSE file)
// Design license: CC-BY 4.0 https://creativecommons.org/licenses/by/4.0/

import type { ExpressionSpecification, LayerSpecification } from "maplibre-gl";
import {
  CYCLING_LAYER,
  CYCLING_ROUTES_LAYER,
  CYCLING_ROUTES_MAX_ZOOM,
  osmFeatureId,
} from "@transitopia/cycling-core/tiles.ts";
import type { Comfort } from "@transitopia/cycling-core/classify.ts";
import type { Theme } from "@transitopia/map-style/basemap.ts";
import { FONTS } from "@transitopia/map-style/basemap.ts";
import {
  defaultLineLayout,
  interpolateZoom,
} from "@transitopia/map-style/zoom.ts";

// Which map "source" file (which .pmtiles file) the cycling data layers are found in
export const mapSource = "transitopia-cycling";
/**
 * The same tiles again for bike routes, which they hold only to CYCLING_ROUTES_MAX_ZOOM: a source of
 * its own, so MapLibre overzooms them beyond it (packages/cycling-core/README.md#tiles).
 */
export const routesSource = "transitopia-cycling-routes";

export const routesSourceSpec = (url: string) =>
  ({ type: "vector", url, maxzoom: CYCLING_ROUTES_MAX_ZOOM }) as const;

/** Ferries, drawn just over the basemap's water, under its bridges (CyclingMap.tsx); the other layers go over the basemap. */
export const FERRY_LAYER = "cycling_ferry";

/** Layers with cycling paths, as opposed to other things like bike rack locations */
export const pathLayerIds = [
  "cycling_path_1",
  "cycling_path_2",
  "cycling_path_3",
  "cycling_path_4",
  "cycling_path_construction",
  FERRY_LAYER,
];
export const otherLayerIds = ["bike_parking_point"];

/**
 * What the cycling layers panel shows (CyclingLayers.tsx): paths at each of the tiles' comfort
 * levels (`comfort4` … `comfort1`: packages/cycling-core/src/classify.ts, named in comfort.ts), and
 * the rest. Ferries that take bikes (class "ferry", no comfort) have a switch of their own.
 */
export interface CyclingShow {
  comfort4: boolean;
  comfort3: boolean;
  comfort2: boolean;
  comfort1: boolean;
  parking: boolean;
  construction: boolean;
  ferries: boolean;
}

export const DEFAULT_SHOW: CyclingShow = {
  comfort4: true,
  comfort3: true,
  comfort2: true,
  comfort1: true,
  parking: true,
  construction: true,
  ferries: true,
};

/** The switch for paths at a comfort level. */
export const comfortKey = (c: Comfort) => `comfort${c}` as const;

const comfort: ExpressionSpecification = ["to-number", ["get", "comfort"]];
const isLine: ExpressionSpecification = ["==", ["geometry-type"], "LineString"];
const notConstruction: ExpressionSpecification = [
  "!=",
  ["get", "construction"],
  true,
];

/** Paths (not under construction) at a comfort level the panel shows. */
function shownPaths(show: CyclingShow): ExpressionSpecification {
  const levels = ([4, 3, 2, 1] as const).filter((c) => show[comfortKey(c)]);
  return ["in", comfort, ["literal", levels]];
}

/** Each layer's filter for what the panel shows. */
export function cyclingFilters(
  show: CyclingShow,
): Record<string, ExpressionSpecification> {
  const paths = shownPaths(show);
  const level = (n: number): ExpressionSpecification => [
    "all",
    isLine,
    ["==", comfort, n],
    notConstruction,
    paths,
  ];
  const oneway: ExpressionSpecification = [
    "any",
    ["==", ["get", "oneway"], 1],
    ["==", ["get", "oneway"], -1],
  ];
  return {
    [FERRY_LAYER]: [
      "all",
      isLine,
      ["==", ["get", "class"], "ferry"],
      show.ferries,
    ],
    cycling_path_construction: [
      "all",
      isLine,
      ["==", ["get", "construction"], true],
      show.construction,
    ],
    cycling_path_4: level(4),
    cycling_path_3: level(3),
    cycling_path_3_inner: level(3),
    cycling_path_2: level(2),
    cycling_path_1: level(1),
    cycling_path_oneway_arrows_white: [
      "all",
      isLine,
      oneway,
      [">=", comfort, 3],
      notConstruction,
      paths,
    ],
    cycling_path_oneway_arrows_green: [
      "all",
      isLine,
      oneway,
      ["<=", comfort, 2],
      notConstruction,
      paths,
    ],
    cycling_path_name: [
      "all",
      isLine,
      ["in", ["get", "class"], ["literal", ["track", "lane"]]],
      ["has", "name"],
      [">=", comfort, 2],
      paths,
    ],
    bike_parking_point: [
      "all",
      ["==", ["geometry-type"], "Point"],
      ["==", ["get", "amenity"], "bicycle_parking"],
      show.parking,
    ],
  };
}

/** Selected and hovered paths, and the selected bike route. */
const HIGHLIGHT = "rgba(200, 100, 100, 1)";

/**
 * Helper: the resulting color should normally be 'color',
 * but make it salmon colored when hovered or selected.
 */
const colorWithHoverAndSelectionStates = (color: string) =>
  [
    "case",
    ["boolean", ["feature-state", "selected"], false],
    HIGHLIGHT,
    ["boolean", ["feature-state", "hover"], false],
    HIGHLIGHT,
    color,
  ] satisfies ExpressionSpecification;

/** The selected bike route's highlight: drawn under the paths, its ways and its subroutes' whole. */
export const ROUTE_HIGHLIGHT_LAYER = "cycling_route_selected";

/**
 * The highlight's layer, showing the route with this relation id (none: hidden, so MapLibre
 * doesn't load the routes' tiles). Add it before the paths' layers.
 */
export function routeHighlightLayer(
  relation: number | undefined,
): LayerSpecification {
  return {
    id: ROUTE_HIGHLIGHT_LAYER,
    type: "line",
    source: routesSource,
    "source-layer": CYCLING_ROUTES_LAYER,
    layout: { ...defaultLineLayout, ...routeHighlightLayout(relation) },
    filter: routeHighlightFilter(relation),
    paint: {
      "line-color": HIGHLIGHT,
      "line-opacity": 0.7,
      "line-width": interpolateZoom({ z6: 3, z10: 6, z16: 18 }),
    },
  };
}

export const routeHighlightFilter = (
  relation: number | undefined,
): ExpressionSpecification => [
  "==",
  ["id"],
  relation === undefined ? -1 : osmFeatureId("relation", relation),
];

export const routeHighlightLayout = (relation: number | undefined) => ({
  visibility: relation === undefined ? ("none" as const) : ("visible" as const),
});

/** Colours per theme. Light is the original design; dark keeps the same meaning on a dark basemap. */
const PALETTE = {
  light: {
    path: "rgba(26, 109, 35, 1)",
    pathLeastComfortable: "rgba(26, 50, 35, 1)",
    pathInner: "rgba(255, 255, 255, 0.6)",
    construction: "rgba(226, 109, 35, 1)",
    ferry: "rgba(74, 125, 178, 1)",
    name: "rgba(24, 79, 19, 1)",
    nameHalo: "rgba(231, 231, 131, 0.72)",
    parkingFill: "rgba(231, 231, 131, 0.72)",
  },
  dark: {
    path: "rgba(76, 194, 106, 1)",
    pathLeastComfortable: "rgba(167, 201, 174, 1)",
    pathInner: "rgba(21, 24, 28, 0.7)",
    construction: "rgba(240, 138, 60, 1)",
    ferry: "rgba(127, 169, 212, 1)",
    name: "rgba(184, 240, 194, 1)",
    nameHalo: "rgba(21, 24, 28, 0.85)",
    parkingFill: "rgba(70, 80, 40, 0.9)",
  },
} satisfies Record<Theme, Record<string, string>>;

export function cyclingLayers(
  theme: Theme,
  show: CyclingShow,
): LayerSpecification[] {
  const c = PALETTE[theme];
  const filters = cyclingFilters(show);
  const layers: LayerSpecification[] = [
    {
      // Long dashes, unlike the paths' short ones: a crossing, not a facility.
      id: FERRY_LAYER,
      type: "line",
      source: mapSource,
      "source-layer": CYCLING_LAYER,
      layout: {
        "line-cap": "butt",
        "line-join": "round",
        visibility: "visible",
      },
      paint: {
        "line-color": colorWithHoverAndSelectionStates(c.ferry),
        "line-width": interpolateZoom({ z6: 1, z10: 1.5, z16: 3 }),
        "line-dasharray": [4, 3],
      },
    },
    {
      id: "cycling_path_construction",
      type: "line",
      source: mapSource,
      "source-layer": CYCLING_LAYER,
      layout: {
        "line-cap": "butt",
        "line-join": "bevel",
        visibility: "visible",
      },
      paint: {
        "line-color": colorWithHoverAndSelectionStates(c.construction),
        "line-width": interpolateZoom({ z10: 3, z16: 4 }),
        "line-dasharray": [0.4, 1],
      },
    },
    {
      id: "cycling_path_4",
      type: "line",
      source: mapSource,
      "source-layer": CYCLING_LAYER,
      layout: defaultLineLayout,
      paint: {
        "line-color": colorWithHoverAndSelectionStates(c.path),
        "line-width": interpolateZoom({ z10: 2, z16: 8 }),
      },
    },
    {
      id: "cycling_path_3",
      type: "line",
      source: mapSource,
      "source-layer": CYCLING_LAYER,
      layout: defaultLineLayout,
      paint: {
        "line-color": colorWithHoverAndSelectionStates(c.path),
        "line-width": interpolateZoom({ z10: 2, z16: 7 }),
      },
    },
    {
      // Draw white centre lines overtop of "comfort level 3" cycling paths
      id: "cycling_path_3_inner",
      type: "line",
      source: mapSource,
      "source-layer": CYCLING_LAYER,
      minzoom: 13,
      layout: defaultLineLayout,
      paint: {
        "line-color": c.pathInner,
        "line-width": 2,
        "line-dasharray": [2, 4],
      },
    },
    {
      id: "cycling_path_2",
      type: "line",
      source: mapSource,
      "source-layer": CYCLING_LAYER,
      layout: defaultLineLayout,
      paint: {
        "line-color": colorWithHoverAndSelectionStates(c.path),
        "line-width": interpolateZoom({ z10: 3, z16: 6 }),
        // dashed lines when zoomed in greater than 12
        "line-dasharray": {
          type: "interval",
          stops: [
            [0, [1]],
            [12, [0.4, 1.4]],
          ],
        },
      },
    },
    {
      id: "cycling_path_1",
      type: "line",
      source: mapSource,
      "source-layer": CYCLING_LAYER,
      minzoom: 8,
      layout: defaultLineLayout,
      paint: {
        "line-color": colorWithHoverAndSelectionStates(c.pathLeastComfortable),
        "line-width": interpolateZoom({ z10: 2, z16: 4 }),
        "line-dasharray": [0.3, 2],
      },
    },
    {
      id: "cycling_path_oneway_arrows_white",
      type: "symbol",
      source: mapSource,
      "source-layer": CYCLING_LAYER,
      minzoom: 8,
      paint: {
        "icon-opacity": interpolateZoom({ z1: 0, z14: 0, z15: 0.9 }),
      },
      layout: {
        "icon-image": "transitopia:chevron-white",
        "icon-rotate": 90,
        "icon-size": 0.6,
        "icon-allow-overlap": true,
        visibility: "visible",
        "symbol-spacing": 40,
        "symbol-placement": "line",
      },
    },
    {
      id: "cycling_path_oneway_arrows_green",
      type: "symbol",
      source: mapSource,
      "source-layer": CYCLING_LAYER,
      minzoom: 8,
      paint: {
        "icon-opacity": interpolateZoom({ z1: 0, z14: 0, z15: 0.9 }),
      },
      layout: {
        "icon-image": "transitopia:chevron-green",
        // Rotate the icon 90° for the common case, one way in the direction of the street.
        // Rotate it 270° for the rare case, one way in the opposite direction of the street
        "icon-rotate": ["case", ["==", ["get", "oneway"], 1], 90, 270],
        "icon-size": 0.8,
        "icon-allow-overlap": true,
        visibility: "visible",
        "symbol-spacing": 40,
        "symbol-placement": "line",
      },
    },
    {
      id: "cycling_path_name",
      type: "symbol",
      source: mapSource,
      "source-layer": CYCLING_LAYER,
      layout: {
        "symbol-placement": "line",
        "symbol-spacing": 350,
        "text-field": "{name}",
        "text-font": [...FONTS.regular],
        "text-max-angle": 30,
        "text-pitch-alignment": "viewport",
        "text-rotation-alignment": "map",
        "text-offset": [0, 1.1],
        "text-size": 10,
        "text-transform": "uppercase",
        visibility: "visible",
      },
      paint: {
        "text-color": c.name,
        "text-halo-blur": 1,
        "text-halo-color": c.nameHalo,
        "text-halo-width": 2,
      },
    },
    // Bike parking (points)
    {
      id: "bike_parking_point",
      type: "circle",
      source: mapSource,
      "source-layer": CYCLING_LAYER,
      layout: {
        visibility: "visible",
        "circle-sort-key": 1,
      },
      paint: {
        "circle-radius": interpolateZoom({ z8: 1, z12: 1, z16: 3 }),
        "circle-color": c.parkingFill,
        "circle-stroke-width": interpolateZoom({ z8: 0.5, z12: 0.5, z16: 2 }),
        "circle-stroke-color": colorWithHoverAndSelectionStates(c.path),
        "circle-stroke-opacity": interpolateZoom({
          z12: 0,
          z14: 0.5,
          z16: 0.8,
        }),
      },
    },
  ];
  // Every layer here is a vector layer with an entry in cyclingFilters.
  return layers.map(
    (l) => ({ ...l, filter: filters[l.id]! }) as LayerSpecification,
  );
}
